/**
 * Faixa de composição, abaixo do cabeçalho da empresa.
 *
 * HISTÓRICO DESTE ARQUIVO (importa para não regredir)
 * ----------------------------------------------------
 * A primeira versão mostrava 5 indicadores do total MAIS 5 de cada lado: 15
 * números no mesmo peso visual, antes da árvore de contas. O usuário leu a tela
 * e disse "tem muito número jogado na tela, não entendi, ficou muito bagunçado".
 *
 * A literatura de dashboard converge no diagnóstico: a memória de trabalho
 * segura ~4 blocos, e a recomendação é 3–7 métricas primárias com o resto em
 * progressive disclosure. 15 no mesmo nível não é densidade, é ruído.
 *
 * O DESENHO ATUAL, em três camadas
 * ---------------------------------
 *   1. TRÊS números grandes: total, conciliado, em aberto — a pergunta
 *      "quanto tem, quanto fechou, quanto falta".
 *   2. UMA barra: a proporção entre eles. Proporção é forma, não número; tirar
 *      isso do texto poupa o leitor de comparar três valores de 8 dígitos.
 *   3. Os dois lados (a receber / a pagar) em UMA linha cada, com barra própria.
 *
 * O resto — sem par por natureza, títulos ligados, nº de contas, % casado —
 * vive atrás de "Ver detalhes". São números de investigação, não de leitura.
 *
 * POR QUE ELA NÃO REPETE O "TOTAL DÉBITO / TOTAL CRÉDITO" DE CIMA
 * ---------------------------------------------------------------
 * Aquele par soma os DOIS LADOS DO MESMO LANÇAMENTO em todas as analíticas do
 * balancete. Por partida dobrada é idêntico por construção: medido em
 * 09/10/2026, 60 de 60 empresas "fecham", e nenhum dos 673.930 lançamentos do
 * ano tem lado nulo. Ele confirma que a escrituração é balanceada e nada mais.
 *
 * Esta faixa mede outra coisa: das contas que COMPÕEM saldo, quanto do título
 * já foi ligado a um pagamento identificado.
 *
 * Toda regra de leitura vive em `lados.ts` (função pura, testada).
 */

import React from 'react';
import { Box, Collapse, Flex, Text, Tooltip } from '@chakra-ui/react';
import { CaretDown, CaretRight, Info } from 'phosphor-react';

import { GlassCard } from '../../../../components/glass';
import { IContaDetalhe, ILadoComposicao } from './conta/papelTrabalho';
import {
  EXPLICA_LADO,
  ROTULO_LADO,
  descreveBarra,
  fatiasDaBarra,
  ladosVisiveis,
  pctConciliado,
  somaNaoFecha,
  textoCobertura,
  valeQuebrarPorLado,
} from './lados';
import {
  contaComoLado,
  contaSemMovimento,
  contasRestantes,
  contasVisiveis,
  pctDaConta,
  rotuloConta,
} from './contasDaComposicao';

const COR = {
  ink: '#0F172A',
  muted: '#64748B',
  faint: '#94A3B8',
  linha: 'rgba(15, 23, 42, 0.08)',
  trilho: 'rgba(15, 23, 42, 0.06)',
  ok: '#059669',
  aberto: '#1B88F5',
  warn: '#D97706',
  warnBg: 'rgba(217, 119, 6, 0.08)',
  brand: '#1B88F5',
} as const;

const NUM = { fontVariantNumeric: 'tabular-nums' } as const;

const fmt = (n: number | null | undefined): string =>
  n == null || !Number.isFinite(Number(n))
    ? '—'
    : Number(n).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

/** Curto para as linhas de lado: "31,7 mi" em vez de "31.689.953,23". */
const fmtCurto = (n: number | null | undefined): string => {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 1e6) return `${(v / 1e6).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi`;
  if (abs >= 1e3) return `${(v / 1e3).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} mil`;
  return v.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
};

const fmtPct = (n: number | null | undefined): string =>
  n == null || !Number.isFinite(Number(n)) ? '—' : `${Number(n).toFixed(1)}%`;

/**
 * A barra de proporção.
 *
 * Tem `aria-label` e tooltip com os percentuais em texto: barra sem legenda
 * textual é decoração para quem usa leitor de tela, e a cor sozinha também não
 * serve a quem não a distingue — por isso a ordem das fatias é fixa e o texto
 * repete o que a cor mostra.
 */
const Barra: React.FC<{
  l: { conciliado?: number | null; em_aberto?: number | null; sem_par_real?: number | null };
  altura?: string;
}> = ({ l, altura = '7px' }) => {
  const f = fatiasDaBarra(l);
  const descricao = descreveBarra(l);
  if (!f) return <Box h={altura} bg={COR.trilho} borderRadius="full" />;
  return (
    <Tooltip label={descricao} fontSize="11px" placement="top" hasArrow>
      <Flex
        h={altura}
        borderRadius="full"
        overflow="hidden"
        bg={COR.trilho}
        role="img"
        aria-label={descricao}
        cursor="help"
      >
        <Box w={`${f.conciliado}%`} bg={COR.ok} />
        <Box w={`${f.aberto}%`} bg={COR.aberto} opacity={0.55} />
        <Box w={`${f.semPar}%`} bg={COR.warn} opacity={0.45} />
      </Flex>
    </Tooltip>
  );
};

/** Um dos três números grandes da primeira camada. */
/**
 * Um dos três números grandes.
 *
 * `fatia` é a cor da parte correspondente na barra: o número e a faixa colorida
 * passam a dizer a mesma coisa, que foi o que o usuário pediu ao notar que as
 * cores não conversavam. O ponto ao lado do rótulo faz a ligação explícita —
 * cor sozinha não serve a quem não a distingue.
 */
const Principal: React.FC<{
  rotulo: string;
  valor: string;
  fatia?: string;
  ajuda: string;
}> = ({ rotulo, valor, fatia, ajuda }) => (
  <Box flex="1 1 170px" minW="150px">
    <Text fontSize="19px" fontWeight="700" color={fatia ?? COR.ink} sx={NUM} lineHeight="1.2">
      {valor}
    </Text>
    <Flex align="center" gap={1} mt={0.5}>
      {fatia && <Box w="7px" h="7px" borderRadius="full" bg={fatia} flexShrink={0} />}
      <Text fontSize="10.5px" color={COR.muted} fontWeight="500">
        {rotulo}
      </Text>
      <Tooltip label={ajuda} fontSize="11px" placement="top" hasArrow>
        <Box color={COR.faint} cursor="help" display="flex">
          <Info size={10} />
        </Box>
      </Tooltip>
    </Flex>
  </Box>
);

/** Uma linha de lado: nome, valor curto, barra e o % conciliado. */
const LinhaLado: React.FC<{ l: ILadoComposicao }> = ({ l }) => {
  const pct = pctConciliado(Number(l.conciliado ?? 0), Number(l.provisionado ?? 0));
  return (
    <Flex align="center" gap={3} py={1.5}>
      <Flex align="center" gap={1} w="104px" minW="104px">
        <Text fontSize="11.5px" fontWeight="600" color={COR.ink} whiteSpace="nowrap">
          {ROTULO_LADO[l.lado]}
        </Text>
        <Tooltip label={EXPLICA_LADO[l.lado]} fontSize="11px" placement="top" hasArrow>
          <Box color={COR.faint} cursor="help" display="flex">
            <Info size={10} />
          </Box>
        </Tooltip>
      </Flex>
      {/* curto na tela, completo no title: a precisão continua a um hover */}
      {/* o total do lado fica em tinta neutra: ele é a SOMA das três fatias,
          e pintá-lo de uma das cores sugeriria que pertence a uma delas */}
      <Text
        fontSize="12px"
        color={COR.ink}
        sx={NUM}
        w="78px"
        minW="78px"
        textAlign="right"
        title={fmt(l.provisionado)}
      >
        {fmtCurto(l.provisionado)}
      </Text>
      <Box flex="1" minW="90px">
        <Barra l={l} altura="6px" />
      </Box>
      <Text
        fontSize="11.5px"
        fontWeight="700"
        color={pct == null ? COR.faint : COR.ok}
        sx={NUM}
        w="52px"
        minW="52px"
        textAlign="right"
      >
        {fmtPct(pct)}
      </Text>
    </Flex>
  );
};

/** Um par rótulo/valor do bloco de detalhes. */
const Detalhe: React.FC<{ rotulo: string; valor: string; cor?: string }> = ({
  rotulo,
  valor,
  cor,
}) => (
  <Box flex="1 1 150px" minW="130px">
    <Text
      fontSize="9.5px"
      fontWeight="700"
      color={COR.faint}
      textTransform="uppercase"
      letterSpacing="0.06em"
    >
      {rotulo}
    </Text>
    <Text fontSize="13px" fontWeight="600" color={cor ?? COR.ink} sx={NUM} mt={0.5}>
      {valor}
    </Text>
  </Box>
);

/**
 * Uma conta da lista. Mesma linguagem visual das linhas de lado: valor curto,
 * barra e %. Decisão do usuário — a versão com todas as colunas voltaria a
 * empilhar números.
 */
const LinhaConta: React.FC<{
  c: IContaDetalhe;
  onAbrir?: (conta: string) => void;
}> = ({ c, onAbrir }) => {
  const vazia = contaSemMovimento(c);
  const pct = pctDaConta(c);
  return (
    <Flex
      as={onAbrir ? 'button' : 'div'}
      w="100%"
      textAlign="left"
      align="center"
      gap={3}
      py={1.5}
      px={1}
      opacity={vazia ? 0.5 : 1}
      cursor={onAbrir ? 'pointer' : 'default'}
      _hover={{ bg: onAbrir ? 'rgba(27, 136, 245, 0.05)' : undefined }}
      onClick={onAbrir ? () => onAbrir(c.conta) : undefined}
      title={onAbrir ? 'Abrir o papel de trabalho desta conta' : undefined}
    >
      <Text fontSize="11.5px" color={COR.ink} flex="1" minW="120px" noOfLines={1}>
        {rotuloConta(c)}
      </Text>
      <Text
        fontSize="11.5px"
        color={COR.ink}
        sx={NUM}
        w="74px"
        minW="74px"
        textAlign="right"
        title={fmt(c.provisionado)}
      >
        {fmtCurto(c.provisionado)}
      </Text>
      <Box flex="1" minW="70px" maxW="160px">
        <Barra l={contaComoLado(c)} altura="5px" />
      </Box>
      <Text
        fontSize="11px"
        fontWeight="700"
        color={pct == null ? COR.faint : COR.ok}
        sx={NUM}
        w="48px"
        minW="48px"
        textAlign="right"
      >
        {fmtPct(pct)}
      </Text>
    </Flex>
  );
};

interface IProps {
  lados?: ILadoComposicao[];
  total?: Omit<ILadoComposicao, 'lado'>;
  /** Texto do backend quando o lote ainda não rodou para esta empresa. */
  aviso?: string;
  carregando?: boolean;
  /** As contas que compõem, para a lista do "Ver detalhes". */
  contas?: IContaDetalhe[];
  /** Abre o papel de trabalho da conta. */
  onAbrirConta?: (conta: string) => void;
  /** O ano contábil destes números. */
  exercicio?: number;
  /** `sob_demanda` = calculado agora, porque o lote só grava o corrente. */
  origem?: 'lote' | 'sob_demanda';
  /** `true` = faltou conta ou estourou o tempo. */
  parcial?: boolean;
}

export const FaixaComposicao: React.FC<IProps> = ({
  lados,
  total,
  aviso,
  carregando,
  contas,
  onAbrirConta,
  exercicio,
  origem,
  parcial,
}) => {
  const [aberto, setAberto] = React.useState(false);
  const [paginas, setPaginas] = React.useState(1);

  if (carregando) return null;

  const visiveis = ladosVisiveis(lados);

  /*
    O aviso SOZINHO (sem número) é o caso "o lote ainda não calculou": aí ele
    é a mensagem inteira da faixa.

    Com número, NÃO pode retornar aqui — um cálculo parcial traz aviso E
    valores, e devolver só o texto esconderia os números que o contador tem.
    Nesse caso o aviso aparece como faixa de alerta acima dos valores.
  */
  if (aviso && (!visiveis.length || !total)) {
    return (
      <GlassCard intensity="medium" p={4} mb={5}>
        <Flex align="flex-start" gap={2}>
          <Box color={COR.warn} mt="1px">
            <Info size={13} />
          </Box>
          <Text fontSize="11.5px" color={COR.warn} lineHeight="1.5">
            {aviso}
          </Text>
        </Flex>
      </GlassCard>
    );
  }

  if (!visiveis.length || !total) return null;

  const quebrar = valeQuebrarPorLado(lados);
  const naoFecha = somaNaoFecha(visiveis, total);
  const pctTotal = pctConciliado(Number(total.conciliado ?? 0), Number(total.provisionado ?? 0));
  const temNatureza = Number(total.sem_par_natureza ?? 0) > 0;
  const visiveisContas = contasVisiveis(contas, paginas);
  const restantes = contasRestantes(contas, paginas);

  return (
    <GlassCard intensity="medium" p={4} mb={5}>
      <Flex align="center" gap={2} mb={2.5}>
        <Text
          fontSize="10px"
          fontWeight="700"
          color={COR.muted}
          textTransform="uppercase"
          letterSpacing="0.06em"
        >
          Composição de saldo
        </Text>
        {/*
          O EXERCÍCIO, ao lado do título.

          Sem isto a faixa é ambígua: o contador trocava o ano, via números
          diferentes e não tinha como saber a qual ano se referiam. E quando o
          número é calculado na hora (exercício que o lote não gravou), ele
          precisa saber — senão a demora de alguns segundos parece defeito.
        */}
        {exercicio != null && (
          <Tooltip
            label={
              origem === 'sob_demanda'
                ? 'O cálculo em lote cobre apenas o exercício corrente. Este ano foi calculado agora, a partir dos lançamentos do razão.'
                : 'Calculado pelo processamento noturno, a partir de 1º de janeiro deste exercício.'
            }
            fontSize="11px"
            placement="top"
            hasArrow
          >
            <Text
              fontSize="10px"
              fontWeight="700"
              color={origem === 'sob_demanda' ? COR.warn : COR.muted}
              bg={origem === 'sob_demanda' ? COR.warnBg : 'rgba(15, 23, 42, 0.05)'}
              px={1.5}
              borderRadius="full"
              cursor="help"
              sx={NUM}
            >
              {exercicio}
              {origem === 'sob_demanda' ? ' · calculado agora' : ''}
            </Text>
          </Tooltip>
        )}
        <Tooltip
          label="Das contas que compõem saldo: quanto do título já foi ligado a um pagamento identificado. Não é o TOTAL DÉBITO/CRÉDITO acima, que soma os dois lados do mesmo lançamento e por isso sempre fecha."
          fontSize="11px"
          placement="top"
          hasArrow
        >
          <Box color={COR.faint} cursor="help" display="flex">
            <Info size={11} />
          </Box>
        </Tooltip>
      </Flex>

      {naoFecha && (
        <Flex align="flex-start" gap={2} bg={COR.warnBg} borderRadius="8px" px={3} py={2} mb={3}>
          <Box color={COR.warn} mt="1px">
            <Info size={13} />
          </Box>
          <Text fontSize="11px" color={COR.warn} lineHeight="1.5">
            A soma dos lados não fecha com o total. Os números abaixo medem a mesma coisa por réguas
            diferentes — confira antes de usar.
          </Text>
        </Flex>
      )}

      {/*
        Cálculo incompleto (conta que falhou, teto de contas ou tempo esgotado).
        Número parcial calado é pior que número nenhum: o contador somaria uma
        faixa que não representa a empresa inteira.
      */}
      {parcial && !naoFecha && (
        <Flex align="flex-start" gap={2} bg={COR.warnBg} borderRadius="8px" px={3} py={2} mb={3}>
          <Box color={COR.warn} mt="1px">
            <Info size={13} />
          </Box>
          <Text fontSize="11px" color={COR.warn} lineHeight="1.5">
            {aviso ??
              'O cálculo deste exercício ficou incompleto. Os números abaixo podem não representar a empresa inteira.'}
          </Text>
        </Flex>
      )}

      {/* 1ª camada: os três números que respondem à pergunta principal. */}
      <Flex gap={5} wrap="wrap">
        <Principal
          rotulo="Total provisionado"
          valor={fmt(total.provisionado)}
          ajuda="O título gerado no período: venda a prazo, nota de entrada. É o universo que pode ser conciliado. A barra abaixo mostra como ele se divide."
        />
        <Principal
          rotulo={pctTotal == null ? 'Conciliado' : `Conciliado · ${fmtPct(pctTotal)}`}
          valor={fmt(total.conciliado)}
          fatia={Number(total.conciliado) > 0 ? COR.ok : COR.faint}
          ajuda="Quanto do título já foi pago E ligado a um pagamento identificado. É a faixa verde da barra."
        />
        <Principal
          rotulo="Em aberto"
          valor={fmt(total.em_aberto)}
          fatia={Number(total.em_aberto) > 0 ? COR.aberto : COR.faint}
          ajuda="O que ainda falta receber ou pagar. É a faixa azul da barra."
        />
        <Principal
          rotulo="Não conciliado"
          valor={fmt(total.sem_par_real)}
          fatia={Number(total.sem_par_real) > 0 ? COR.warn : COR.faint}
          ajuda="Baixa que não achou título, já descontado o que não tem par por natureza. É a faixa âmbar da barra."
        />
      </Flex>

      {/* 2ª camada: a proporção, que nenhum dos três números acima mostra. */}
      <Box mt={3}>
        <Barra l={total} />
      </Box>

      {/* 3ª camada: os dois lados, uma linha cada. */}
      {quebrar && (
        <Box mt={3} pt={2.5} borderTop="1px solid" borderColor={COR.linha}>
          {visiveis.map(l => (
            <LinhaLado key={l.lado} l={l} />
          ))}
        </Box>
      )}

      {/* O resto é número de investigação: fica atrás de um clique. */}
      <Flex
        as="button"
        align="center"
        gap={1}
        mt={2.5}
        onClick={() => setAberto(v => !v)}
        aria-expanded={aberto}
        _hover={{ color: COR.brand }}
        color={COR.muted}
      >
        {aberto ? <CaretDown size={11} /> : <CaretRight size={11} />}
        <Text fontSize="11px" fontWeight="600">
          {aberto ? 'Menos detalhes' : 'Ver detalhes'}
        </Text>
      </Flex>

      <Collapse in={aberto} animateOpacity>
        <Flex gap={4} wrap="wrap" pt={3}>
          {temNatureza && (
            <Detalhe rotulo="Sem par por natureza" valor={fmt(total.sem_par_natureza)} />
          )}
          <Detalhe rotulo="Títulos ligados" valor={textoCobertura(total)} />
          <Detalhe rotulo="% casado" valor={fmtPct(total.pct_casado)} />
          <Detalhe rotulo="Contas que compõem" valor={String(total.contas ?? 0)} />
        </Flex>
        {temNatureza && (
          <Text fontSize="10.5px" color={COR.muted} pt={2} lineHeight="1.6">
            <b>Sem par por natureza</b> é compensação de cartão e transferência do próprio grupo:
            não existe título individual para casar, então não é falha de conciliação. O{' '}
            <b>% casado</b> já desconta esse valor.
          </Text>
        )}

        {/*
          A lista de CONTAS. Pedido do usuário: "uma lista para mostrar todas
          as contas (com um botão de ver mais para paginar) e a porcentagem de
          conciliação de cada conta".
        */}
        {!!visiveisContas.length && (
          <Box pt={4} mt={3} borderTop="1px solid" borderColor={COR.linha}>
            <Text
              fontSize="9.5px"
              fontWeight="700"
              color={COR.faint}
              textTransform="uppercase"
              letterSpacing="0.06em"
              mb={1.5}
            >
              Contas que compõem ({contas?.length ?? 0})
            </Text>
            {visiveisContas.map(c => (
              <LinhaConta key={c.conta} c={c} onAbrir={onAbrirConta} />
            ))}
            {restantes > 0 && (
              <Flex
                as="button"
                align="center"
                gap={1}
                mt={1.5}
                onClick={() => setPaginas(n => n + 1)}
                color={COR.brand}
              >
                <CaretDown size={11} />
                <Text fontSize="11px" fontWeight="600">
                  Ver mais ({restantes})
                </Text>
              </Flex>
            )}
          </Box>
        )}
      </Collapse>
    </GlassCard>
  );
};

export default FaixaComposicao;
